import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import "./dashboard-table.component.scss";
import PREVIEW_ICON from "../../../assets/images/preview.svg";
import PREVIEW_DISABLED from "../../../assets/images/preview-disabled.svg";
import CHECK_GREEN from "../../../assets/images/check-green.svg";
import CHECK_DISABLED from "../../../assets/images/check-disabled.svg";
import NOT_COMPLETE from "../../../assets/images/not-complete.svg";
import { useNavigate } from "react-router-dom";
import IconButton from '@mui/material/IconButton';
import { convertDateTime } from "../../../utility/function.utility";
import { Media } from "../../../model/media.model";
import InfographicPreviewModal from "../../../modal/infographic-preview/infographic-preview";
import MediaService from "../../../services/media.service";
import { useSnackbar } from "../../../utility/snackbar.utility";
import CustomSnackbar from "../../snackbar/snackbar.component";
import ConfirmModal from "../../../modal/confirm/confirm.component";
import ICON_ERROR from "../../../assets/images/circle-error.svg";
import ICON_ERROR_DISABLED from "../../../assets/images/error-disabled.svg";
import Button from "@mui/material/Button";
import { usePaginationFilter } from "../../../utility/pagination-filter";
import TRASH_ICON from "../../../assets/images/trash.svg";
import { useAuth } from "../../../utility/use-auth";
import { UserRole } from "../../../model/user-role.enum";

interface TableWithPaginationProps {
    data: Media[];
    page: number;
    onUpdate: () => void;
}

const DashboardTableWithPagination: React.FC<TableWithPaginationProps> = ({ data, page, onUpdate }) => {

    const navigate = useNavigate();
    const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
    const { limitItem } = usePaginationFilter();
    const { user } = useAuth();

    const [isShowInfoPreviewModal, setIsShowInfoPreviewModal] = useState(false);
    const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
    const [isShowDeleteConfirmModal, setIsShowDeleteConfirmModal] = useState(false);
    const [isShowDelete, setIsShowDelete] = useState(false);
    const [selectedInfoId, setSelectedInfoId] = useState<string>();
    const [selectedMediaId, setSelectedMediaId] = useState<string | null>(null);

    const TRANSCRIPT_ERROR = "ERROR";

    useEffect(() => {
        const isError = data.find(item => item?.transcript_status?.startsWith(TRANSCRIPT_ERROR));
        setIsShowDelete(!!isError);
    }, []);

    const clickRow = (id: string, isAvailable: boolean, status: string | undefined) => {
        if (!isAvailable || status?.startsWith(TRANSCRIPT_ERROR)) return;

        navigate(`detail/${id}`);
    }

    const handleClickPreview = (event, infoId: string | null) => {
        event.stopPropagation();
        if (!infoId) return;

        setSelectedInfoId(infoId);
        setIsShowInfoPreviewModal(true);
    }

    const handleArchive = (event, mediaId: string) => {
        event.stopPropagation();
        setSelectedMediaId(mediaId);
        setIsShowConfirmModal(true);
    }

    const handleCloseConfirmArchiveModal = async (confirm: boolean) => {
        setIsShowConfirmModal(false);
        if (!confirm || !selectedMediaId) return;

        try {
            await MediaService.archiveMedia(selectedMediaId);
            onUpdate();
        } catch (error) {
            showSnackbar('', "error");
        }
    };

    const handleDelete = (event, mediaId: string) => {
        event.stopPropagation();
        setSelectedMediaId(mediaId);
        setIsShowDeleteConfirmModal(true);
    }

    const handleCloseConfirmDeleteModal = async (confirm: boolean) => {
        setIsShowDeleteConfirmModal(false);
        if (!confirm || !selectedMediaId) return;

        try {
            await MediaService.deleteMedia(selectedMediaId);
            onUpdate();
        } catch (error) {
            showSnackbar('', "error");
        }
    };

    const handleRestore = async (event, mediaId: string) => {
        event.stopPropagation();

        try {
            await MediaService.restoreMedia(mediaId);
            onUpdate();
        } catch (error) {
            showSnackbar('', "error");
        }
    }

    return (
        <>
            <div className="dashboard-table-control">
                <Table className="dashboard-table-th-control">
                    <thead>
                        <tr>
                            <th className="text-left">#</th>
                            <th className="text-left dashboard-table-column-topic">รายการวีดีโอ/ไฟล์เสียง</th>
                            <th className="text-left">ผู้อัปโหลด</th>
                            <th>วันที่อัปโหลดวีดีโอ</th>
                            <th>เนื้อหาจากการถอดไฟล์เสียง</th>
                            <th>Infographic</th>
                            <th>ดูตัวอย่าง</th>
                            <th style={{ width: isShowDelete ? '120px' : '80px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr
                                key={row.id}
                                className={`dashboard-table-row-control ${(row.is_transcript_available && !row.transcript_status?.startsWith(TRANSCRIPT_ERROR)) && 'cursor-pointer'} ${row.is_archived && 'text-disabled'}`}
                                onClick={() => clickRow(row.id, row.is_transcript_available, row.transcript_status)}
                            >
                                <td className="text-left">{(page - 1) * limitItem + index + 1}</td>
                                <td className="text-left">{row.topic}</td>
                                <td className="text-left">{row.created_by}</td>
                                <td>{convertDateTime(row.created_at)}</td>
                                <td>
                                    {
                                        (row.transcript_status?.startsWith(TRANSCRIPT_ERROR)) ?
                                            <div className="dashboard-table-status-wrapper">
                                                <img src={row.is_archived ? ICON_ERROR_DISABLED : ICON_ERROR} className="dashboard-table-status-icon me-2" />
                                                <span className="dashboard-table-status-text-error">
                                                    ไม่พร้อมใช้งาน
                                                </span>
                                            </div> :
                                            row.is_transcript_available ?
                                                <div className="dashboard-table-status-wrapper">
                                                    <img src={row.is_archived ? CHECK_DISABLED : CHECK_GREEN} className="dashboard-table-status-icon me-2" />
                                                    <span className="dashboard-table-status-text">พร้อมใช้งาน</span>
                                                </div> :
                                                <span className="dashboard-table-status-text-process">กำลังสร้าง Transcript</span>
                                    }
                                </td>
                                <td>
                                    {
                                        row.is_infographic_available ?
                                            <img src={row.is_archived ? CHECK_DISABLED : CHECK_GREEN} className="dashboard-table-status-icon"></img> :
                                            <img src={NOT_COMPLETE} className="dashboard-table-status-icon"></img>
                                    }
                                </td>
                                <td>
                                    <IconButton
                                        color="primary"
                                        onClick={(event) => handleClickPreview(event, row.info_id)}
                                        disabled={!row.is_infographic_available}
                                    >
                                        <img src={(row.is_infographic_available && !row.is_archived) ? PREVIEW_ICON : PREVIEW_DISABLED} className="dashboard-table-preview-icon"></img>
                                    </IconButton>
                                </td>
                                <td>
                                    <div className="dashboard-table-row-action">
                                        {
                                            row.is_archived ?
                                                <Button
                                                    variant="text"
                                                    className="dashboard-table-restore-button"
                                                    disabled={user?.role === UserRole.STAFF && row.archived_by_role === UserRole.SUPER_ADMIN}
                                                    onClick={(event) => handleRestore(event, row.id)}
                                                >
                                                    นำกลับ
                                                </Button> :
                                                <Button
                                                    variant="text"
                                                    onClick={(event) => handleArchive(event, row.id)}
                                                    disabled={!row.is_transcript_available && !row.transcript_status?.startsWith(TRANSCRIPT_ERROR)}
                                                    className={`dashboard-table-hide-button ${(!row.is_transcript_available && !row.transcript_status?.startsWith(TRANSCRIPT_ERROR)) && 'disabled'}`}
                                                >
                                                    ซ่อน
                                                </Button>
                                        }

                                        {
                                            (row.transcript_status?.startsWith(TRANSCRIPT_ERROR)) &&
                                            <IconButton color="primary" onClick={(event) => handleDelete(event, row.id)}>
                                                <img src={TRASH_ICON} className="detail-content-item-info-top-right-preview-icon"></img>
                                            </IconButton>
                                        }
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {
                isShowInfoPreviewModal && <InfographicPreviewModal show={isShowInfoPreviewModal} onClose={() => setIsShowInfoPreviewModal(false)} infoId={selectedInfoId} />
            }

            {
                isShowConfirmModal && <ConfirmModal show={isShowConfirmModal} type="hide" onClose={handleCloseConfirmArchiveModal} />
            }

            {
                isShowDeleteConfirmModal && <ConfirmModal show={isShowDeleteConfirmModal} type="delete" onClose={handleCloseConfirmDeleteModal} />
            }

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                duration={snackbar.duration}
                onClose={hideSnackbar}
            />
        </>
    );
};

export default DashboardTableWithPagination;
